import React from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const sectors = ["FHA", "ITIN", "VA", "Conventional", "DSCR", "Jumbo", "Refi"];
const incomeTypes = [
  { v: "First_Time_Buyer", l: "First-time buyer" },
  { v: "W2_Employee", l: "W-2 employee" },
  { v: "Self_Employed", l: "Self-employed / 1099" },
  { v: "Investor_DSCR", l: "Investor / rental income" },
  { v: "Cash_Out", l: "Cash-out refinance" },
];

// Parse UTM params (or fallback to Direct/organic)
function useUtm() {
  return React.useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      source_platform: p.get("utm_source") || p.get("src") || "Direct",
      campaign_id: p.get("utm_campaign") || "organic_landing",
      ad_creative_variant: p.get("utm_content") || p.get("variant") || "landing_v1",
    };
  }, []);
}

export default function LeadCaptureForm() {
  const utm = useUtm();
  const [form, setForm] = React.useState({
    full_name: "",
    email: "",
    phone: "",
    location: "",
    timeline_days: 60,
    income_type: "First_Time_Buyer",
    hook_sector: "FHA",
  });
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(null); // { redirect_url, social_lead_id }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/leads/public", {
        ...form,
        timeline_days: parseInt(form.timeline_days),
        ...utm,
      });
      setDone(r.data);
      toast.success("Profile received. Redirecting to your intake…");
      setTimeout(() => { window.location.href = r.data.redirect_url; }, 2200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="border border-success/40 bg-success/[0.04] rounded-sm p-8 text-center" data-testid="lead-capture-success">
        <CheckCircle size={40} weight="fill" className="text-success mx-auto" />
        <h3 className="mt-4 font-heading font-black text-2xl tracking-tighter">You're pre-sorted.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Lead ID <span className="font-mono text-primary">{done.social_lead_id}</span> · redirecting to your secure intake at dwinvestmentgroup.com…
        </p>
        <a href={done.redirect_url} data-testid="manual-redirect-link" className="mt-6 inline-flex items-center gap-2 text-sm text-primary hover:opacity-80">
          Continue now <ArrowRight size={14} weight="bold" />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-white/[0.08] bg-[#0a0a0a] rounded-sm p-6 md:p-8" data-testid="lead-capture-form">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-primary">Secure intake · 30 seconds</div>
          <h3 className="mt-2 font-heading font-black text-2xl md:text-3xl tracking-tighter">Get pre-sorted. Then apply.</h3>
        </div>
        <div className="hidden md:block text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          src · <span className="text-foreground">{utm.source_platform}</span>
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Three quick questions → secure handoff to your DW Mortgage Hub application.</p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Full name</label>
          <input data-testid="lc-name" required value={form.full_name} onChange={set("full_name")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="Jane Rivera" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Email</label>
          <input data-testid="lc-email" type="email" required value={form.email} onChange={set("email")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="you@example.com" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Phone (optional)</label>
          <input data-testid="lc-phone" value={form.phone} onChange={set("phone")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="(901) 555-0110" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Target location <span className="text-primary">·1</span></label>
          <input data-testid="lc-location" required value={form.location} onChange={set("location")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="Memphis, TN" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Timeline <span className="text-primary">·2</span></label>
          <select data-testid="lc-timeline" value={form.timeline_days} onChange={set("timeline_days")} className="w-full bg-[#0a0a0a] border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm">
            <option value={30}>Within 30 days</option>
            <option value={60}>Within 60 days</option>
            <option value={90}>Within 90 days</option>
            <option value={180}>3–6 months</option>
            <option value={365}>Just exploring (12 months+)</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Income type <span className="text-primary">·3</span></label>
          <select data-testid="lc-income" value={form.income_type} onChange={set("income_type")} className="w-full bg-[#0a0a0a] border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm">
            {incomeTypes.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Loan program</label>
          <div className="flex flex-wrap gap-2">
            {sectors.map((s) => (
              <button type="button" key={s} data-testid={`lc-sector-${s.toLowerCase()}`} onClick={() => setForm({ ...form, hook_sector: s })}
                className={`px-3 py-1.5 text-xs font-mono rounded-sm border transition-colors ${form.hook_sector === s ? "border-primary bg-primary/10 text-primary" : "border-white/10 text-muted-foreground hover:text-foreground hover:border-white/25"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button type="submit" disabled={loading} data-testid="lc-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-4 bg-primary text-primary-foreground rounded-sm font-medium primary-glow hover-lift disabled:opacity-60 text-sm">
        {loading ? "Encrypting your profile…" : "Continue to secure intake"} <ArrowRight size={16} weight="bold" />
      </button>
      <div className="mt-3 text-[10px] font-mono text-muted-foreground text-center">
        Encrypted handoff · SOC-2 aligned · Data never sold. NMLS #123456.
      </div>
    </form>
  );
}
